import React, {
  useState, useEffect, useMemo,
} from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import area from '@turf/area';
import centerOfMass from '@turf/center-of-mass';
import booleanContains from '@turf/boolean-contains';
import booleanDisjoint from '@turf/boolean-disjoint';
import { featureCollection } from '@turf/helpers';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import useMap from '../../../../hooks/useMap';

import API from '../../../../utils/api-map';
import olc from '../../../../utils/openlocal';
import { initIPFS } from '../../../../state/ipfs';
import { getStageMonitoringData, sqmToHa } from '../../../../utils/helpers';
import { MAP_EVENTS, ALL_REPORT_TYPE } from '../../../../constans/constans';

import Loader from '../../../Loader';
import MapHeader from '../MapHeader';
import MapInfo from '../MapInfo';

const MonitoringZoneMap = ({
  state,
  setState,
  setShow,
  currentStage,
  uploadedFile,
  setUploadedFile,
  defineProjectReportType,
  acceptableArea,
  hectaresLimit,
  usePreviousZone = false,
  useEntireZone = false,
  resetData,
  hasPreviousZone,
  projectReportType,
}) => {
  const translate = useIntl();
  const { tools, mapRef, utils: { centerCoordinates, moveMap, zoomIn } } = useMap((() => {
    if (usePreviousZone || useEntireZone) return { polygon: false, trash: false };
    return { polygon: true, trash: true };
  })());
  const [newState, setNewState] = useState((() => {
    const clone = structuredClone(state);
    // Reset data if user want to add zone as file, prev zone, entire zone or if recently added zones was entire or prev zone
    if (uploadedFile || usePreviousZone || useEntireZone || projectReportType === ALL_REPORT_TYPE || hasPreviousZone) return resetData(clone);
    return clone;
  })());
  const { targetStageInfo, targetSubZone } = useMemo(() => getStageMonitoringData(newState, currentStage), [newState]);
  const defaultNewPolygon = { region: '', polygon: [], codePlus: '', polygonCoordinate: [], square: 0, subId: '' };
  const [isLoading, setIsLoading] = useState(false);
  const [newPolygon, setNewPolygon] = useState(defaultNewPolygon);
  const [warning, setWarning] = useState({ cause: '', message: '' });
  const minArea = 0.2;
  // console.log(state,
  //   currentStage,
  //   defineProjectReportType,
  //   acceptableArea,
  //   hectaresLimit,
  //   usePreviousZone,
  //   useEntireZone,
  //   hasPreviousZone,
  //   projectReportType, '  state,\n'
  //   + '  setState,\n'
  //   + '  setShow,\n');
  const redrawAllFeatures = (data) => {
    new Promise((resolve) => {
      tools.draw.deleteAll();
      resolve(true);
    }).then(() => {
      tools.draw.add(data.polygon[0]);
      data?.subZones.forEach((item) => {
        tools.draw.add(item.polygon[0]);
      });
      data?.sampleZones.forEach((item) => tools.draw.add(item));
    });
  };

  const isEditableFeature = (feature) => {
    // Disable changing of project initiation polygon. Allow selection of the entire zone
    if (feature.id === newState.polygon[0].id) return useEntireZone;

    // Disable sample plots editing
    const anySamplePlot = newState.sampleZones.find((point) => point.id === feature.id);
    if (anySamplePlot) return false;

    const activeSubZones = newState.stagesInfo.filter((sub) => feature.id === sub.subId);
    // If new feature was added
    if (!activeSubZones.length) return true;
    // Enable editing only unique sub zone that refer to current stage
    if (activeSubZones.length === 1 && activeSubZones[0].subId === targetStageInfo?.subId) return true;
    return false;
  };

  const isOverlappedFeature = (feature) => {
    const isOverlapped = newState.subZones.find((sub) => (
      sub.subId !== targetSubZone?.subId && !booleanDisjoint(feature, sub.polygon[0])
    ));
    return !!isOverlapped;
  };

  const showWaringIfInvalidFeature = (square, feature) => {
    if (!booleanContains(newState.polygon[0], feature)) {
      setWarning({ cause: 'outside-polygon', message: translate.formatMessage({ id: 'wizardForm.step2.map.insideWarning' }) });
      return;
    }
    if (+square < minArea) {
      setWarning({
        cause: 'small-zone',
        message: (
          <FormattedMessage
            id="wizardForm.step2.map.smallMonitoringArea"
            values={{ hectaresLimit: minArea, br: <br /> }}
          />
        ),
      });
      return;
    }
    if (!acceptableArea(square)) {
      setWarning({ cause: 'square-limit', message: translate.formatMessage({ id: 'wizardForm.step2.map.hectaresLimit' }, { hectaresLimit }) });
      return;
    }
    if (isOverlappedFeature(feature)) {
      setWarning({ cause: 'overlap', message: translate.formatMessage({ id: 'wizardForm.step2.map.overlap' }) });
      return;
    }
    setWarning({ cause: '', message: '' });
  };

  const paintFeature = (feature, color = 'blue') => {
    feature.properties.polygonColor = color;
    tools.draw.setFeatureProperty(feature.id, 'polygonColor', color);
  };

  const createFeatureCollection = (data) => {
    const features = data.map((el) => el.polygon[0]);
    const collection = featureCollection(features);
    return collection;
  };

  const updateData = async (e, update = false) => {
    const features = update ? e : e.features[0];
    if (!isEditableFeature(features)) {
      redrawAllFeatures(newState);
      return;
    }
    const center = centerOfMass(features).geometry.coordinates;
    const { placeName } = await API.getRegionNameFromPosition(center);
    const roundedArea = sqmToHa(area(features), 3);
    const stageInfo = {
      stage: currentStage,
      subId: features.id,
      filesCidDir: '',
      additional: '',
    };
    const subInfo = {
      subId: features.id,
      polygon: [features],
      polygonCoordinate: features.geometry.coordinates,
      codePlus: olc.encode(center[1], center[0]),
      square: roundedArea,
      region: placeName,
    };

    const updStagesInfo = [...newState.stagesInfo];
    const updSubZones = [...newState.subZones];

    // If enitre zone was chosen
    if (useEntireZone) {
      if (e.type === MAP_EVENTS.CREATE || e.type === MAP_EVENTS.UPDATE || e.type === MAP_EVENTS.DELETE) {
        redrawAllFeatures(newState);
        return;
      }
      updStagesInfo.push(stageInfo);
      updSubZones.push(subInfo);
      const updState = { ...newState, stagesInfo: updStagesInfo, subZones: updSubZones };
      setNewPolygon(subInfo);
      setNewState(updState);
      return;
    }

    // Section, where map action was triggered (draw, update, move, delete)
    switch (e.type) {
      case MAP_EVENTS.CREATE: {
        // If subzone exist, delete the drawn zone
        if (targetSubZone) {
          tools.draw.delete(features.id);
          return;
        }
        updStagesInfo.push(stageInfo);
        updSubZones.push(subInfo);
        paintFeature(features);
        showWaringIfInvalidFeature(roundedArea, features);
        setNewPolygon(subInfo);
        setNewState({ ...newState, stagesInfo: updStagesInfo, subZones: updSubZones });
        return;
      }
      case MAP_EVENTS.UPDATE: {
        updStagesInfo.splice(-1, 1, stageInfo);
        updSubZones.splice(-1, 1, subInfo);
        const updSampleZones = [...newState.sampleZones].filter((item) => item.subId !== targetSubZone.subId);
        const updState = { ...newState, stagesInfo: updStagesInfo, subZones: updSubZones, sampleZones: updSampleZones };
        paintFeature(features);
        showWaringIfInvalidFeature(roundedArea, features);
        setNewPolygon(subInfo);
        setNewState(updState);
        redrawAllFeatures(updState);
        return;
      }
      case MAP_EVENTS.DELETE: {
        updStagesInfo.pop();
        updSubZones.pop();
        const updSampleZones = [...newState.sampleZones].filter((item) => item.subId !== targetSubZone.subId);
        const updState = { ...newState, stagesInfo: updStagesInfo, subZones: updSubZones, sampleZones: updSampleZones };
        setWarning({ cause: '', message: '' });
        setNewPolygon(defaultNewPolygon);
        setNewState(updState);
        redrawAllFeatures(updState);
        return;
      }
      default:
        // If subzone polygon was added as a file
        updStagesInfo.push(stageInfo);
        updSubZones.push(subInfo);
        showWaringIfInvalidFeature(roundedArea, features);
        setNewPolygon({ ...subInfo });
        setNewState({ ...newState, stagesInfo: updStagesInfo, subZones: updSubZones });
        paintFeature(features);
    }
  };

  const choosePreviousZone = (data) => {
    const lastSubZone = data.subZones.slice(-1)[0];
    const updStagesInfo = [...data.stagesInfo];
    updStagesInfo.push({ stage: currentStage, subId: lastSubZone.subId, filesCidDir: '', additional: '' });
    setNewState({ ...data, stagesInfo: updStagesInfo });
  };

  const switchMapEventListeners = (method) => {
    tools.map[method](MAP_EVENTS.MOVE, moveMap);
    tools.map[method](MAP_EVENTS.CREATE, updateData);
    tools.map[method](MAP_EVENTS.DELETE, updateData);
    tools.map[method](MAP_EVENTS.UPDATE, updateData);
  };

  const drawDefaultDataSubZones = () => {
    new Promise((resolve) => {
      // Draw nft zone, all sub zones with sample plots
      redrawAllFeatures(newState);
      resolve(true);
    }).then(() => {
      if (usePreviousZone) choosePreviousZone(newState);
      // If sub zone is added with file
      if (uploadedFile) {
        tools.draw.add(uploadedFile[0]);
        updateData(uploadedFile[0], true);
      }
      zoomIn(newState.polygon[0]);
    });
  };

  const saveScreenshot = (cid) => {
    const updSubZone = [...newState.subZones];
    const target = updSubZone.pop();
    target.cidSubScreenShot = `/ipfs/${cid}`;
    updSubZone.push(target);
    setState({ ...newState, subZones: updSubZone });
  };

  const saveData = () => {
    const canvas = tools.map.getCanvas();
    canvas.toBlob(async (blob) => {
      try {
        const ipfs = await initIPFS();
        const res = await ipfs.add(blob);
        if (!res) throw new Error();
        saveScreenshot(res.path);
        defineProjectReportType(useEntireZone);
        setIsLoading(false);
        setShow(false);
      } catch (e) {
        console.log(e);
      }
    });
  };

  const scaleBeforeSave = () => {
    setIsLoading(true);
    if (usePreviousZone) zoomIn(createFeatureCollection(newState.subZones));
    else zoomIn(newPolygon.polygon[0]);
    if (tools.map.isZooming()) tools.map.once(MAP_EVENTS.ZOOM_END, saveData);
    else saveData();
  };

  useEffect(() => {
    if (!tools) return;
    try {
      drawDefaultDataSubZones();
    } catch (e) {
      console.log(e);
    }
    return () => setUploadedFile();
  }, [tools]);

  useEffect(() => {
    if (!tools) return;
    switchMapEventListeners('on');
    return () => switchMapEventListeners('off');
  }, [tools, newState, newPolygon]);

  return (
    <div className="map">
      {isLoading && <div className="cropper__loader"><Loader /></div>}
      <div className="map__box">
        <MapHeader coordinates={centerCoordinates} handleClose={() => setShow(false)} />
        <div ref={mapRef} className="map__map" />
        <MapInfo
          data={newPolygon}
          handleClick={() => scaleBeforeSave()}
          viewType="monitoring-map"
          extra={{ warning, useEntireZone, usePreviousZone }}
        />
      </div>
    </div>
  );
};

export default MonitoringZoneMap;
